import React, { useMemo } from "react";
import ReactFlow, { Background, Controls, MiniMap, MarkerType } from "reactflow";
import "reactflow/dist/style.css";

/**
 * Project 4: Software Approval System
 * - Organization endpoints send hash -> AWS App account (ALB, EKS, RDS)
 * - Cross-account hash verification -> Security account (CarbonBlack/CrowdStrike)
 * - Result -> Approval workflow + alerts
 */

const nodeStyle = {
  section: {
    background: "#ffffff",
    border: "2px solid #cbd5e1",
    borderRadius: 12,
    padding: 8,
    fontWeight: 800,
    color: "#0f172a",
    width: 240,
    textAlign: "center",
  },
  compute: {
    background: "#e7f5ff",
    border: "2px solid #1c7ed6",
    borderRadius: 12,
    padding: 10,
    fontWeight: 700,
    color: "#0b2e59",
    width: 140,
    textAlign: "center",
  },
  data: {
    background: "#fff0f6",
    border: "2px solid #a61e4d",
    borderRadius: 12,
    padding: 10,
    fontWeight: 700,
    color: "#4a0b2a",
    width: 140,
    textAlign: "center",
  },
  security: {
    background: "#fef2f2",
    border: "2px solid #ef4444",
    borderRadius: 12,
    padding: 10,
    fontWeight: 700,
    color: "#7f1d1d",
    width: 150,
    textAlign: "center",
  },
  ops: {
    background: "#f3fff6",
    border: "2px solid #2f9e44",
    borderRadius: 12,
    padding: 10,
    fontWeight: 700,
    color: "#0f3d1f",
    width: 140,
    textAlign: "center",
  },
  desktop: {
    background: "#fff7ed",
    border: "2px solid #fb923c",
    borderRadius: 12,
    padding: 10,
    fontWeight: 700,
    color: "#7c2d12",
    width: 110,
    textAlign: "center",
  },
  net: {
    background: "#e3fafc",
    border: "2px solid #1098ad",
    borderRadius: 12,
    padding: 10,
    fontWeight: 700,
    color: "#05333b",
    width: 130,
    textAlign: "center",
  },
  note: {
    background: "#fffbeb",
    border: "2px solid #f59e0b",
    borderRadius: 10,
    padding: 8,
    fontWeight: 700,
    color: "#92400e",
    width: 180,
    textAlign: "center",
  },
};

function mkEdge(id, source, target, label, opts = {}) {
  return {
    id,
    source,
    target,
    label,
    type: "smoothstep",
    animated: opts?.animated ?? false,
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 18,
      height: 18,
      color: opts?.style?.stroke ?? "#64748b",
    },
    labelStyle: { fontWeight: 700, fill: "#0f172a" },
    labelBgStyle: { fill: "#ffffff" },
    labelBgPadding: [6, 4],
    style: {
      strokeWidth: 4,
      stroke: "#64748b",
      ...(opts?.style ?? {}),
    },
    ...opts,
  };
}

export default function Project4FlowDiagram() {
  const nodes = useMemo(
    () => [
      // Background containers
      {
        id: "bg-org",
        position: { x: 40, y: 60 },
        data: { label: "" },
        style: {
          width: 340,
          height: 260,
          background: "#fff7ed40",
          border: "3px solid #fdba74",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-app",
        position: { x: 440, y: 40 },
        data: { label: "" },
        style: {
          width: 520,
          height: 360,
          background: "#eff6ff40",
          border: "3px solid #93c5fd",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-sec",
        position: { x: 1040, y: 80 },
        data: { label: "" },
        style: {
          width: 300,
          height: 260,
          background: "#fef2f240",
          border: "3px solid #fca5a5",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },

      // Organization endpoints
      {
        id: "sec-org",
        position: { x: 70, y: 70 },
        data: { label: "ORG - ENDPOINTS" },
        style: { ...nodeStyle.section, width: 180, borderColor: "#fb923c" },
      },
      { id: "ep1", position: { x: 70, y: 130 }, data: { label: "EP 1" }, style: nodeStyle.desktop },
      { id: "ep2", position: { x: 220, y: 130 }, data: { label: "EP 2" }, style: nodeStyle.desktop },
      { id: "ep3", position: { x: 70, y: 210 }, data: { label: "EP 3" }, style: nodeStyle.desktop },
      { id: "epn", position: { x: 220, y: 210 }, data: { label: "EP ..." }, style: nodeStyle.desktop },
      { id: "send-hash", position: { x: 280, y: 320 }, data: { label: "① Send File Hash" }, style: nodeStyle.note },

      // AWS App account
      {
        id: "sec-app",
        position: { x: 470, y: 55 },
        data: { label: "AWS ACCOUNT 1 - APPLICATION" },
        style: { ...nodeStyle.section, width: 300 },
      },
      { id: "alb", position: { x: 520, y: 125 }, data: { label: "ALB" }, style: nodeStyle.net },
      {
        id: "eks",
        position: { x: 500, y: 185 },
        data: { label: "EKS Cluster" },
        style: nodeStyle.section,
      },
      { id: "flask", position: { x: 510, y: 255 }, data: { label: "Flask" }, style: nodeStyle.compute },
      { id: "react", position: { x: 700, y: 255 }, data: { label: "React" }, style: nodeStyle.compute },
      { id: "rds", position: { x: 610, y: 335 }, data: { label: "MongoDB" }, style: nodeStyle.data },

      // Security account
      {
        id: "sec-sec",
        position: { x: 1060, y: 95 },
        data: { label: "AWS ACCOUNT 2 - SECURITY" },
        style: { ...nodeStyle.section, width: 220, borderColor: "#fca5a5" },
      },
      { id: "cb", position: { x: 1080, y: 170 }, data: { label: "CarbonBlack" }, style: nodeStyle.security },
      { id: "cs", position: { x: 1080, y: 260 }, data: { label: "CrowdStrike" }, style: nodeStyle.security },
      {
        id: "hash-verify",
        position: { x: 900, y: 210 },
        data: { label: "② Hash Verify" },
        style: { ...nodeStyle.note, width: 140, padding: 6 },
      },

      // Workflow & notifications
      { id: "result", position: { x: 720, y: 430 }, data: { label: "③ Result" }, style: nodeStyle.note },
      { id: "approval", position: { x: 610, y: 520 }, data: { label: "Approval" }, style: nodeStyle.ops },
      { id: "alerts", position: { x: 770, y: 520 }, data: { label: "Alerts" }, style: nodeStyle.ops },
    ],
    []
  );

  const edges = useMemo(() => {
    const blue = { stroke: "#2563eb" };
    const red = { stroke: "#ef4444" };
    const green = { stroke: "#16a34a" };

    return [
      mkEdge("e-ep1-send", "ep1", "send-hash", "", { style: { ...blue } }),
      mkEdge("e-ep2-send", "ep2", "send-hash", "", { style: { ...blue } }),
      mkEdge("e-ep3-send", "ep3", "send-hash", "", { style: { ...blue } }),
      mkEdge("e-epn-send", "epn", "send-hash", "", { style: { ...blue } }),
      mkEdge("e-send-alb", "send-hash", "alb", "ingest", { style: { ...blue, strokeWidth: 5 } }),
      mkEdge("e-alb-eks", "alb", "eks", "", { style: { ...blue } }),
      mkEdge("e-eks-flask", "eks", "flask", "", { style: { ...blue } }),
      mkEdge("e-eks-react", "eks", "react", "", { style: { ...blue } }),
      mkEdge("e-eks-rds", "eks", "rds", "", { style: { ...blue } }),
      mkEdge("e-eks-hash", "eks", "hash-verify", "② Hash Verify", {
        animated: true,
        style: { ...red, strokeWidth: 5, strokeDasharray: "10 8" },
      }),
      mkEdge("e-hash-cb", "hash-verify", "cb", "", { style: { ...red } }),
      mkEdge("e-hash-cs", "hash-verify", "cs", "", { style: { ...red } }),
      mkEdge("e-eks-result", "eks", "result", "③ Result", {
        animated: true,
        style: { ...green, strokeWidth: 5, strokeDasharray: "10 8" },
      }),
      mkEdge("e-result-approval", "result", "approval", "", { style: { ...green } }),
      mkEdge("e-result-alerts", "result", "alerts", "", { style: { ...green } }),
    ];
  }, []);

  return (
    <div style={{ width: "100%", height: "720px", background: "#ffffff" }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        fitViewOptions={{ padding: 0.12 }}
        defaultEdgeOptions={{
          type: "smoothstep",
          markerEnd: { type: MarkerType.ArrowClosed },
        }}
        minZoom={0.4}
        maxZoom={2}
        zoomOnScroll={false}
        zoomOnPinch={false}
        panOnScroll={false}
        preventScrolling={true}
      >
        <Background gap={18} size={1} color="#e5e7eb" />
        <MiniMap pannable zoomable />
        <Controls />
      </ReactFlow>
    </div>
  );
}
